import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints, FactGrid, Callout, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { SequencedRequest, resolveRace } from './cantonModel';
import { PartyChip } from './cantonVisuals';

export const meta = {
    title: 'Synchronizers: Ordering Without Seeing',
    tagline: 'Agreeing on sequence without reading the contents.',
};

// ---------------------------------------------------------------------------
// Double-spend race
// ---------------------------------------------------------------------------

const RACE_REQUESTS: SequencedRequest[] = [
    { id: 'R1', recipient: 'Bob' },
    { id: 'R2', recipient: 'Carol' },
];

const RaceDemo = () => {
    const colors = useColors();
    const [order, setOrder] = useState<'bob' | 'carol'>('bob');
    const ordered = order === 'bob' ? RACE_REQUESTS : [...RACE_REQUESTS].reverse();
    const results = resolveRace(ordered, 7);

    return (
        <Panel>
            <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', lineHeight: 1.65, mb: 2 }}>
                Alice owns Iou <strong>#7</strong> ($100, issued by Bank) and submits two transfers of it almost
                simultaneously: <strong>R1</strong> to Bob and <strong>R2</strong> to Carol. Both are valid on their own.
                The sequencer can't read either one, so it can't know they conflict. It just stamps them in the
                order they arrive.
            </Typography>
            <MiniLabel>Which request reached the sequencer first?</MiniLabel>
            <SegmentedToggle<'bob' | 'carol'>
                ariaLabel="Arrival order"
                value={order}
                onChange={setOrder}
                options={[{ value: 'bob', label: 'R1 (to Bob) first' }, { value: 'carol', label: 'R2 (to Carol) first' }]}
            />
            <Box sx={{ mt: 2, display: 'grid', gap: '10px' }} aria-live="polite">
                {results.map((result) => (
                    <Box key={result.request.id} sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '130px 1fr' },
                        gap: '6px 14px',
                        p: '12px 14px',
                        borderRadius: '8px',
                        border: `1px solid ${result.committed ? colors.accent.green : colors.accent.orange}`,
                        backgroundColor: colors.bg.primary,
                    }}>
                        <Typography sx={{ color: colors.text.muted, fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>
                            {result.timestamp}
                        </Typography>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', mb: 0.5 }}>
                                <Typography sx={{ color: colors.text.primary, fontWeight: 800 }}>{result.request.id}: transfer #7 to</Typography>
                                <PartyChip party={result.request.recipient} />
                                <Typography sx={{ color: result.committed ? colors.accent.green : colors.accent.orange, fontWeight: 800, fontSize: '0.85rem' }}>
                                    {result.committed ? 'COMMITTED' : 'REJECTED'}
                                </Typography>
                            </Box>
                            <Typography sx={{ color: colors.text.secondary, fontSize: '0.86rem', lineHeight: 1.55 }}>{result.note}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', lineHeight: 1.65, mt: 2 }}>
                Either order is fine. What matters is that there is <em>one</em> order, and that every involved node
                sees the same one, so Alice's node and Bank's node independently reach the same verdict about which
                transfer won.
            </Typography>
        </Panel>
    );
};

// ---------------------------------------------------------------------------
// Protocol walkthrough
// ---------------------------------------------------------------------------

type Actor = 'alice' | 'bob' | 'seq' | 'med' | 'carol';

const ACTORS: Record<Actor, { label: string; sub: string; x: number; y: number }> = {
    alice: { label: 'Alice\'s node', sub: 'submitter', x: 100, y: 60 },
    bob: { label: 'Bob\'s node', sub: 'counterparty', x: 540, y: 60 },
    seq: { label: 'Sequencer', sub: 'orders + delivers', x: 320, y: 168 },
    med: { label: 'Mediator', sub: 'collects verdicts', x: 320, y: 305 },
    carol: { label: 'Carol\'s node', sub: 'not involved', x: 540, y: 305 },
};

interface ProtocolStep {
    title: string;
    body: string;
    active: Actor[];
    arrows: [Actor, Actor][];
    seqKnows: string;
    medKnows: string;
}

const STEPS: ProtocolStep[] = [
    {
        title: 'Interpret',
        body: 'Alice\'s node runs the DAML code for the Transfer and builds the full transaction. It splits it into views, one per group of informees, and encrypts each view so only the nodes hosting those informees can read it.',
        active: ['alice'],
        arrows: [],
        seqKnows: 'Nothing yet.',
        medKnows: 'Nothing yet.',
    },
    {
        title: 'Submit',
        body: 'Alice\'s node sends a confirmation request to the sequencer: the encrypted views, addressed to Bob\'s node (and to itself), plus a stripped-down informee tree for the mediator, in which every view\'s contents are replaced by a hash.',
        active: ['alice', 'seq'],
        arrows: [['alice', 'seq']],
        seqKnows: 'A message from Alice\'s node, its size, and its recipients. The payload is ciphertext.',
        medKnows: 'Nothing yet.',
    },
    {
        title: 'Sequence & deliver',
        body: 'The sequencer gives the request a timestamp and a place in its single total order, then delivers each piece only to its addressed recipients. Carol\'s node gets nothing.',
        active: ['seq', 'alice', 'bob', 'med'],
        arrows: [['seq', 'alice'], ['seq', 'bob'], ['seq', 'med']],
        seqKnows: 'Sender, recipients, timestamp, size. Still no contents.',
        medKnows: 'Which parties must confirm which (hashed) views. No contract data.',
    },
    {
        title: 'Validate',
        body: 'Alice\'s and Bob\'s nodes each decrypt their own view and re-run it: does it conform to the DAML code, is it properly authorized, are all input contracts still active? Inputs are locked so a later request in the order can\'t spend them too.',
        active: ['alice', 'bob'],
        arrows: [],
        seqKnows: 'Unchanged: metadata only.',
        medKnows: 'Unchanged: it\'s waiting for responses.',
    },
    {
        title: 'Confirm',
        body: 'Each confirming node sends its approve or reject response to the mediator, again through the sequencer. A response says "my parties accept this view", not what the view contains.',
        active: ['alice', 'bob', 'med'],
        arrows: [['alice', 'med'], ['bob', 'med']],
        seqKnows: 'That two small responses were sent to the mediator.',
        medKnows: 'Who approved and who rejected.',
    },
    {
        title: 'Verdict',
        body: 'Once every required confirmation is in, the mediator broadcasts the verdict (commit, or reject if anyone rejected or timed out) to all involved nodes via the sequencer.',
        active: ['med', 'alice', 'bob'],
        arrows: [['med', 'alice'], ['med', 'bob']],
        seqKnows: 'That a verdict went out.',
        medKnows: 'The outcome. Still no contract data.',
    },
    {
        title: 'Commit',
        body: 'Alice\'s and Bob\'s nodes apply the result to their own stores: the old Iou is archived, the new one created. Both reach the same state without any node ever having seen the whole network, and Carol\'s node never learned any of it happened.',
        active: ['alice', 'bob'],
        arrows: [],
        seqKnows: 'Metadata only, start to finish.',
        medKnows: 'Structure and verdicts only, start to finish.',
    },
];

const BOX_W = 170;
const BOX_H = 56;

// Trim a centre-to-centre line so it starts and ends at the edges of the boxes.
const edgePoint = (fromX: number, fromY: number, toX: number, toY: number) => {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const scale = Math.min((BOX_W / 2 + 6) / Math.abs(dx || 1e-9), (BOX_H / 2 + 6) / Math.abs(dy || 1e-9));
    return [fromX + dx * scale, fromY + dy * scale];
};

const ProtocolWalkthrough = () => {
    const colors = useColors();
    const [index, setIndex] = useState(0);
    const step = STEPS[index];
    const markerId = 'canton-protocol-arrow';

    return (
        <Panel>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: 2 }} role="tablist" aria-label="Protocol steps">
                {STEPS.map((item, stepIndex) => (
                    <ButtonBase
                        key={item.title}
                        role="tab"
                        aria-selected={stepIndex === index}
                        onClick={() => setIndex(stepIndex)}
                        sx={{
                            px: '10px',
                            py: '5px',
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            border: `1px solid ${stepIndex === index ? colors.accent.cyan : colors.border.default}`,
                            color: stepIndex === index ? colors.accent.cyan : stepIndex < index ? colors.text.primary : colors.text.muted,
                            backgroundColor: stepIndex === index ? colors.accent.cyanGlow : 'transparent',
                        }}
                    >
                        {stepIndex + 1}. {item.title}
                    </ButtonBase>
                ))}
            </Box>

            <Box component="svg" viewBox="0 0 640 360" role="img" aria-label={`Step ${index + 1}: ${step.title}`} sx={{ width: '100%', height: 'auto', display: 'block' }}>
                <defs>
                    <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.accent.cyan} />
                    </marker>
                </defs>
                {step.arrows.map(([from, to]) => {
                    const a = ACTORS[from];
                    const b = ACTORS[to];
                    const [x1, y1] = edgePoint(a.x, a.y, b.x, b.y);
                    const [x2, y2] = edgePoint(b.x, b.y, a.x, a.y);
                    return (
                        <line key={`${from}-${to}`} x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke={colors.accent.cyan} strokeWidth={3} markerEnd={`url(#${markerId})`} />
                    );
                })}
                {(Object.keys(ACTORS) as Actor[]).map((key) => {
                    const actor = ACTORS[key];
                    const on = step.active.includes(key);
                    return (
                        <g key={key} opacity={key === 'carol' ? 0.4 : on ? 1 : 0.55} style={{ transition: 'opacity 0.25s ease' }}>
                            <rect x={actor.x - BOX_W / 2} y={actor.y - BOX_H / 2} width={BOX_W} height={BOX_H} rx={10} fill={colors.bg.primary} />
                            <rect x={actor.x - BOX_W / 2} y={actor.y - BOX_H / 2} width={BOX_W} height={BOX_H} rx={10}
                                fill={on ? colors.accent.cyanGlow : 'transparent'}
                                stroke={on ? colors.accent.cyan : colors.border.strong}
                                strokeWidth={on ? 2 : 1}
                                strokeDasharray={key === 'carol' ? '5 5' : undefined} />
                            <text x={actor.x} y={actor.y - 2} textAnchor="middle" fontSize={19} fontWeight={800} fill={colors.text.primary}>{actor.label}</text>
                            <text x={actor.x} y={actor.y + 18} textAnchor="middle" fontSize={13} fill={colors.text.secondary}>{actor.sub}</text>
                        </g>
                    );
                })}
                <rect x={222} y={128} width={196} height={218} rx={14} fill="none" stroke={colors.border.default} strokeDasharray="4 6" />
                <text x={320} y={242} textAnchor="middle" fontSize={13} fill={colors.text.muted}>synchronizer</text>
            </Box>

            <Box sx={{ mt: 1.5 }} aria-live="polite">
                <Typography sx={{ color: colors.text.primary, fontWeight: 800, mb: 0.5 }}>{index + 1}. {step.title}</Typography>
                <Typography sx={{ color: colors.text.secondary, fontSize: '0.92rem', lineHeight: 1.65, mb: 1.5 }}>{step.body}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: '10px' }}>
                    {[['Sequencer knows', step.seqKnows], ['Mediator knows', step.medKnows], ['Carol\'s node knows', 'Nothing. It is never a recipient.']].map(([label, text]) => (
                        <Box key={label} sx={{ p: '10px 12px', borderRadius: '8px', border: `1px solid ${colors.border.subtle}`, backgroundColor: colors.bg.secondary }}>
                            <MiniLabel>{label}</MiniLabel>
                            <Typography sx={{ color: colors.text.secondary, fontSize: '0.86rem', lineHeight: 1.5 }}>{text}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button variant="outlined" disabled={index === 0} onClick={() => setIndex(index - 1)}
                    sx={{ borderColor: colors.border.default, color: colors.text.secondary }}>
                    Back
                </Button>
                <Button variant="outlined" onClick={() => setIndex((index + 1) % STEPS.length)}
                    sx={{ borderColor: colors.accent.cyan, color: colors.accent.cyan }}>
                    {index === STEPS.length - 1 ? 'Start over' : 'Next step'}
                </Button>
            </Box>
        </Panel>
    );
};

// ---------------------------------------------------------------------------
// Several synchronizers
// ---------------------------------------------------------------------------

const MultiSyncDiagram = () => {
    const colors = useColors();
    const syncs = [
        { id: 'x', label: 'Market synchronizer', sub: 'run by one operator', x: 170, y: 50 },
        { id: 'g', label: 'Global Synchronizer', sub: 'decentralized, BFT', x: 470, y: 50 },
    ];
    const nodes = [
        { label: 'Bank node', x: 80, links: ['x'] },
        { label: 'Custodian node', x: 245, links: ['x', 'g'] },
        { label: 'Registry node', x: 405, links: ['g'] },
        { label: 'Carol\'s node', x: 565, links: ['g'] },
    ];
    return (
        <Box component="svg" viewBox="0 0 640 250" role="img"
            aria-label="Bank node connects to a market synchronizer; Registry and Carol's nodes connect to the Global Synchronizer; the Custodian node connects to both"
            sx={{ width: '100%', height: 'auto', display: 'block', my: 2 }}>
            {nodes.flatMap((node) => node.links.map((link) => {
                const sync = syncs.find((item) => item.id === link)!;
                const both = node.links.length > 1;
                return <line key={`${node.label}-${link}`} x1={node.x} y1={180} x2={sync.x} y2={80}
                    stroke={both ? colors.accent.cyan : colors.border.strong} strokeWidth={both ? 3 : 2} />;
            }))}
            {syncs.map((sync) => (
                <g key={sync.id}>
                    <rect x={sync.x - 125} y={22} width={250} height={58} rx={12} fill={colors.bg.primary} stroke={colors.accent.cyan} strokeWidth={2} />
                    <text x={sync.x} y={48} textAnchor="middle" fontSize={18} fontWeight={800} fill={colors.text.primary}>{sync.label}</text>
                    <text x={sync.x} y={68} textAnchor="middle" fontSize={13} fill={colors.text.secondary}>{sync.sub}</text>
                </g>
            ))}
            {nodes.map((node) => (
                <g key={node.label}>
                    <rect x={node.x - 74} y={180} width={148} height={46} rx={10} fill={colors.bg.primary}
                        stroke={node.links.length > 1 ? colors.accent.cyan : colors.border.strong} strokeWidth={node.links.length > 1 ? 2 : 1} />
                    <text x={node.x} y={209} textAnchor="middle" fontSize={16} fontWeight={700} fill={colors.text.primary}>{node.label}</text>
                </g>
            ))}
        </Box>
    );
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - the core role of a synchronizer
//   Q1 - synchronizers see commitments, not plaintext content
//   Q2 - why more than one synchronizer can coexist
export const blocks: StageBlock[] = [
    content('the-problem', () => (
        <StageSection gradientBorder eyebrow="Stage 5 of 8" title="If nobody sees everything, who agrees on the order?">
            <Prose>
                On a public blockchain, ordering comes for free: every node sees every transaction, and consensus
                produces a single chain of blocks everyone agrees on. Canton has taken that away. Each node sees only
                its own slice of the ledger, yet the nodes that <em>share</em> a contract still have to agree on what
                happened to it and in what order. Otherwise the same Iou could be spent twice, with each spend
                looking valid to a different node.
            </Prose>
            <Prose>
                That's the job of a <strong>synchronizer</strong> (called a "domain" in older Canton versions). It's
                the coordination layer that participant nodes connect to, and it's made of two main services:
            </Prose>
            <FactGrid items={[
                ['Sequencer', 'Puts every message into a single total order with a timestamp, and delivers it to exactly the recipients it\'s addressed to. Think of a post office that stamps and forwards sealed envelopes.'],
                ['Mediator', 'Coordinates each transaction\'s approval: collects the involved nodes\' confirm/reject responses and announces the final verdict. Think of a notary counting signatures without reading the document.'],
            ]} />
            <Prose>
                The simplest way to see why ordering matters is a double spend:
            </Prose>
            <RaceDemo />
        </StageSection>
    )),

    question(0),

    content('commitments-not-content', () => (
        <StageSection title="It orders sealed envelopes, not contracts">
            <Prose>
                The synchronizer never receives plaintext contract data. The submitting node encrypts each view of the
                transaction for the nodes allowed to read it, and binds the pieces together with cryptographic hashes
                (<strong>commitments</strong>) so they can't be mixed up or swapped. The sequencer orders and
                forwards ciphertext; the mediator sees which parties must approve which hashed views, and their
                answers. The participants who are actually authorized to see a contract decrypt and validate it
                themselves: the synchronizer's job is ordering and coordination, not understanding.
            </Prose>
            <Prose>
                Step through a full transfer from Alice to Bob below and watch what each actor learns along the way.
            </Prose>
            <ProtocolWalkthrough />
            <FactGrid items={[
                ['The synchronizer can', 'Order messages, timestamp them, deliver them, and see metadata: who sent what to whom, when, and how big.'],
                ['The synchronizer can\'t', 'Read contract contents, or forge or alter a transaction: views are signed by the submitting node and re-validated by every recipient.'],
            ]} />
            <Callout tone="warning" title="What you still trust the synchronizer for">
                A misbehaving synchronizer can't make your node accept an invalid transaction, but it can refuse to
                deliver messages, delay them, or go offline. In other words, it's trusted for{' '}
                <strong>liveness</strong> and fair ordering, not for correctness. That's why important synchronizers
                (like the Global Synchronizer) are run by several independent operators using Byzantine fault
                tolerant (BFT) ordering rather than by a single company.
            </Callout>
        </StageSection>
    )),

    question(1),

    content('multiple-synchronizers', () => (
        <StageSection title="More than one synchronizer can exist">
            <Prose>
                Unlike a blockchain, Canton has no requirement for a single ordering service. Different institutions
                or use cases can run their own synchronizers: a market operator might run one for its members, a
                consortium might share one, and the public Canton Network provides the{' '}
                <strong>Global Synchronizer</strong>, operated as a decentralized service by a group of super
                validators, which validators across the network connect to.
            </Prose>
            <MultiSyncDiagram />
            <Prose>
                A participant node can connect to several synchronizers at once, like the Custodian node above. Each
                contract is assigned to one synchronizer at a time, and each transaction is ordered by exactly one,
                but contracts can move between them. That's what stops separate deployments from becoming isolated
                islands, and it's the theme the composability stage picks back up.
            </Prose>
            <KeyPoints items={[
                <>Privacy (Stage 3) is about who <em>sees</em> what; the synchronizer is about who agrees on <em>ordering</em>.</>,
                <>Together, they're how Canton gets shared, tamper-evident agreement without full public visibility.</>,
                <>Several synchronizers can coexist, and nodes that share one can transact through it.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
